import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, X, Send, Maximize2, Minimize2, MessageSquarePlus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

export interface FloatingAiChatMessage {
  role: 'user' | 'ai';
  text: string;
}

export interface FloatingAiChatProps {
  /** Whether the chat panel is open */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether the chat is in expanded (large) mode */
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;

  messages: FloatingAiChatMessage[];
  loading: boolean;

  input: string;
  onInputChange: (value: string) => void;
  /** Called when user sends a message (Enter key or Send button). If a prompt is passed, send that instead of the current input. */
  onSend: (prompt?: string) => void;
  onReset: () => void;

  /** Subtitle line below the header (e.g. "Context: Applications, Scores, Positions") */
  contextLabel?: string;
  /** Placeholder text for the input field */
  placeholder?: string;
  /** Suggested prompts shown when there are no messages yet */
  suggestedPrompts?: string[];
}

/**
 * FloatingAiChat — shared floating AI Assistant chat panel.
 *
 * Used by ApplicationsPage and PositionDetailPage so that any UI/UX change is
 * applied in a single place. Keeps state (messages, input, loading, etc.) in
 * the parent so each consumer can wire its own backend / store.
 */
export function FloatingAiChat({
  open,
  onOpenChange,
  expanded,
  onExpandedChange,
  messages,
  loading,
  input,
  onInputChange,
  onSend,
  onReset,
  contextLabel,
  placeholder,
  suggestedPrompts = [],
}: FloatingAiChatProps) {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  // v3.5 follow-up (26/05/26): tutte le label fisse della chat AI ora
  // passano da t() — la chat usa la lingua che l'utente ha scelto nel
  // profilo (Firestore users.preferredLanguage). Il default del
  // `placeholder` resta opzionale per backward compat — se i parent
  // non lo passano si usa la copy generica localizzata.
  const resolvedPlaceholder = placeholder ?? t('aiChat.placeholder.default');

  useEffect(() => {
    if (open) {
      setVisible(true);
      const r = requestAnimationFrame(() => setAnimating(true));
      return () => cancelAnimationFrame(r);
    } else if (visible) {
      setAnimating(false);
      const t = setTimeout(() => setVisible(false), 120);
      return () => clearTimeout(t);
    }
  }, [open, visible]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    if (window.matchMedia('(max-width: 639px)').matches) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <div
        className={cn(
          'fixed bottom-4 right-4 z-40 transition-all duration-100 sm:bottom-6 sm:right-6',
          open ? 'opacity-0 scale-50 pointer-events-none' : 'opacity-100 scale-100',
        )}
      >
        <div className="pointer-events-none absolute inset-0 rounded-full bg-primary/70 blur-lg animate-glow-flicker" />
        <Button
          onClick={() => onOpenChange(true)}
          aria-label={t('aiChat.open')}
          className="relative h-12 w-12 rounded-full shadow-lg gap-0 p-0"
        >
          <Sparkles className="h-5 w-5" />
        </Button>
      </div>

      {visible && (
        <>
          <button
            type="button"
            aria-label={t('aiChat.close')}
            className={cn(
              'fixed inset-0 z-40 bg-black/30 sm:hidden h-[100dvh] w-full',
              animating ? 'opacity-100' : 'opacity-0',
            )}
            onClick={() => onOpenChange(false)}
          />

          <div
            role="dialog"
            aria-label={t('aiChat.title')}
            className={cn(
              'fixed z-50 flex flex-col overflow-hidden bg-card shadow-xl border border-border/50',
              'transition-all duration-200 ease-out',
              // Mobile: fill the app window below the sticky header
              'max-sm:left-3 max-sm:right-3',
              'max-sm:top-[calc(3.5rem+env(safe-area-inset-top,0px)+0.375rem)]',
              'max-sm:bottom-[max(0.375rem,env(safe-area-inset-bottom))]',
              'max-sm:rounded-2xl',
              'max-sm:origin-bottom',
              // Desktop: bottom-right floater — height scales with viewport (max cap, not fixed rem)
              'sm:origin-bottom-right sm:bottom-6 sm:right-6 sm:rounded-xl',
              expanded
                ? 'sm:w-[min(28rem,calc(100vw-2rem))] sm:h-[clamp(20rem,calc(100dvh-5rem),calc(100dvh-4rem))] sm:max-h-[calc(100dvh-4rem)]'
                : 'sm:w-[min(22rem,calc(100vw-2rem))] sm:h-[clamp(14rem,calc(100dvh-14rem),calc(100dvh-6rem))] sm:max-h-[calc(100dvh-6rem)]',
              animating ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-2',
            )}
          >
            <div className="shrink-0 px-3 py-2.5 border-b border-border/50 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm font-medium truncate">{t('aiChat.title')}</span>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-7 sm:w-7" onClick={onReset} title={t('aiChat.newChat')}>
                  <MessageSquarePlus className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 sm:h-7 sm:w-7 hidden sm:inline-flex"
                  onClick={() => onExpandedChange(!expanded)}
                  title={expanded ? t('aiChat.minimize') : t('aiChat.expand')}
                >
                  {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 sm:h-7 sm:w-7"
                  onClick={() => onOpenChange(false)}
                  aria-label={t('aiChat.close')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {contextLabel && (
              <p className="shrink-0 w-full px-3 py-1.5 text-[10px] text-muted-foreground bg-muted/40 border-b border-border/30 truncate">
                {contextLabel}
              </p>
            )}

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-3 bg-card">
              {messages.length === 0 && suggestedPrompts.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">{t('aiChat.tryAsking')}</p>
                  {suggestedPrompts.map(prompt => (
                    <button
                      key={prompt}
                      type="button"
                      className="block w-full text-left text-xs p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                      onClick={() => onSend(prompt)}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-xs ${
                      msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                    }`}
                  >
                    {msg.role === 'ai' ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ children }) => <p className="font-semibold">{children}</p>,
                          h2: ({ children }) => <p className="font-semibold">{children}</p>,
                          h3: ({ children }) => <p className="font-semibold">{children}</p>,
                          p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc list-inside mb-1 space-y-0.5">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside mb-1 space-y-0.5">{children}</ol>,
                          li: ({ children }) => <li>{children}</li>,
                          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                          em: ({ children }) => <em className="italic">{children}</em>,
                          code: ({ children }) => <code className="bg-black/10 rounded px-0.5">{children}</code>,
                          table: ({ children }) => (
                            <div className="overflow-x-auto my-1">
                              <table className="w-full text-[11px] border-collapse">{children}</table>
                            </div>
                          ),
                          thead: ({ children }) => <thead className="bg-muted/60">{children}</thead>,
                          th: ({ children }) => (
                            <th className="border border-border/40 px-2 py-1 text-left font-semibold whitespace-nowrap">
                              {children}
                            </th>
                          ),
                          td: ({ children }) => (
                            <td className="border border-border/40 px-2 py-1 align-top">{children}</td>
                          ),
                          tr: ({ children }) => <tr className="even:bg-muted/20">{children}</tr>,
                          a: ({ href, children }) => {
                            const target = href ?? '#';
                            const isInternal = target.startsWith('/');
                            if (isInternal) {
                              return (
                                <Link
                                  to={target}
                                  className="text-primary underline underline-offset-2 hover:text-primary/80"
                                >
                                  {children}
                                </Link>
                              );
                            }
                            return (
                              <a
                                href={target}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary underline underline-offset-2 hover:text-primary/80"
                              >
                                {children}
                              </a>
                            );
                          },
                        }}
                      >
                        {msg.text}
                      </ReactMarkdown>
                    ) : (
                      <span className="whitespace-pre-wrap">{msg.text}</span>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg px-3 py-2">
                    <span className="animate-pulse text-muted-foreground text-xs">●●●</span>
                  </div>
                </div>
              )}
            </div>

            <div className="shrink-0 w-full border-t border-border/50 bg-muted/30 px-3 py-3">
              <div className="flex items-center gap-2">
                <input
                  className="flex-1 min-w-0 h-9 text-sm bg-background rounded-lg px-3 border border-border/40 outline-none focus:ring-1 focus:ring-primary"
                  placeholder={resolvedPlaceholder}
                  value={input}
                  onChange={e => onInputChange(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !loading && onSend()}
                />
                <Button
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={() => onSend()}
                  disabled={!input.trim() || loading}
                  aria-label={t('aiChat.send')}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
